import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ActionIcon,
  CopyButton,
  Group,
  ScrollArea,
  SegmentedControl,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBraces,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconTable,
} from '@tabler/icons-react';
import { useTranslation } from '../i18n/LocaleContext';
import classes from './JsonView.module.css';

type ViewMode = 'code' | 'table';
const VIEW_KEY = 'transpay.json.view';

function loadView(): ViewMode {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'code';
  } catch {
    return 'code';
  }
}
function saveView(v: ViewMode): void {
  try {
    window.localStorage.setItem(VIEW_KEY, v);
  } catch {
    /* ignore */
  }
}

/**
 * JSON viewer used wherever a request/response body, audit snapshot or API
 * example is shown:
 *
 *  - **Copy** button (whole pretty-printed document to the clipboard);
 *  - **JSON** view — syntax highlighted (keys / strings / numbers / booleans /
 *    null), theme-aware, no dependency;
 *  - **Table** view — the document as nested tables: objects become key /
 *    value rows, arrays of objects become real columnar tables (one column
 *    per key), other arrays become index / value rows; nested values render
 *    as nested tables and can be collapsed per node.
 *
 * The chosen view is remembered (localStorage) so every viewer on the site
 * opens the way the user last left it. A string value that itself contains
 * JSON is parsed first so it is highlighted / tabulated rather than shown as
 * one escaped string.
 */
export function JsonView({
  value,
  maxHeight = 280,
  indent = 2,
}: {
  value: unknown;
  /** Max content height before the block scrolls. */
  maxHeight?: number | string;
  indent?: number;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewMode>(() => loadView());
  // Keep every viewer on the page in sync when one of them switches mode.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === VIEW_KEY) setView(loadView());
    };
    const onLocal = () => setView(loadView());
    window.addEventListener('storage', onStorage);
    window.addEventListener('transpay:json-view', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('transpay:json-view', onLocal);
    };
  }, []);
  const changeView = (v: ViewMode) => {
    saveView(v);
    setView(v);
    window.dispatchEvent(new Event('transpay:json-view'));
  };

  const parsed = useMemo(() => normalise(value), [value]);
  const pretty = useMemo(() => toPretty(parsed, indent), [parsed, indent]);
  const nodes = useMemo(() => highlight(pretty), [pretty]);

  return (
    <div className={classes.wrap}>
      <Group justify="flex-end" gap={6} className={classes.toolbar}>
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => changeView(v as ViewMode)}
          data={[
            {
              value: 'code',
              label: (
                <Group gap={4} wrap="nowrap">
                  <IconBraces size={12} />
                  <span>{t('json.viewCode')}</span>
                </Group>
              ),
            },
            {
              value: 'table',
              label: (
                <Group gap={4} wrap="nowrap">
                  <IconTable size={12} />
                  <span>{t('json.viewTable')}</span>
                </Group>
              ),
            },
          ]}
        />
        <CopyButton value={pretty} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('json.copied') : t('json.copy')} withArrow>
              <ActionIcon
                variant={copied ? 'filled' : 'default'}
                color={copied ? 'teal' : undefined}
                size="sm"
                aria-label={t('json.copy')}
                onClick={copy}
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      <ScrollArea.Autosize mah={maxHeight} type="auto" offsetScrollbars>
        {view === 'code' ? (
          <pre className={classes.pre}>
            <code>{nodes}</code>
          </pre>
        ) : (
          <div className={classes.tableWrap}>
            <JsonNode value={parsed} depth={0} />
          </div>
        )}
      </ScrollArea.Autosize>
    </div>
  );
}

// ---------------------------------------------------------------------------
// value helpers
// ---------------------------------------------------------------------------

function normalise(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        /* keep as string */
      }
    }
  }
  return value;
}

function toPretty(value: unknown, indent: number): string {
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value, null, indent) ?? String(value);
  } catch {
    return String(value);
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// ---------------------------------------------------------------------------
// code view
// ---------------------------------------------------------------------------

// string ("..." with escapes) optionally followed by ':' → key; number; true/false; null; punctuation.
const TOKEN =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|(\btrue\b|\bfalse\b)|(\bnull\b)|([{}[\],])/g;

function highlight(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(TOKEN)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const [, str, colon, num, bool, nul, punct] = m;
    if (str !== undefined) {
      out.push(
        <span key={i++} className={colon !== undefined ? classes.key : classes.string}>
          {str}
        </span>,
      );
      if (colon !== undefined) out.push(colon);
    } else if (num !== undefined) {
      out.push(<span key={i++} className={classes.number}>{num}</span>);
    } else if (bool !== undefined) {
      out.push(<span key={i++} className={classes.boolean}>{bool}</span>);
    } else if (nul !== undefined) {
      out.push(<span key={i++} className={classes.null}>{nul}</span>);
    } else if (punct !== undefined) {
      out.push(<span key={i++} className={classes.punct}>{punct}</span>);
    }
    last = idx + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ---------------------------------------------------------------------------
// table view (nested tables)
// ---------------------------------------------------------------------------

const AUTO_EXPAND_DEPTH = 3;

function Primitive({ value }: { value: unknown }) {
  if (value === null) return <span className={classes.null}>null</span>;
  if (value === undefined) return <span className={classes.null}>undefined</span>;
  if (typeof value === 'boolean') return <span className={classes.boolean}>{String(value)}</span>;
  if (typeof value === 'number') return <span className={classes.number}>{String(value)}</span>;
  if (typeof value === 'string') {
    return value === '' ? (
      <span className={classes.null}>""</span>
    ) : (
      <span className={classes.string}>{value}</span>
    );
  }
  return <span className={classes.string}>{String(value)}</span>;
}

/** Renders any JSON value; containers become (collapsible) nested tables. */
function JsonNode({ value, depth }: { value: unknown; depth: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(depth < AUTO_EXPAND_DEPTH);

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className={classes.null}>[] · {t('json.empty')}</span>;
    const summary = `[${t('json.items', { n: value.length })}]`;
    return (
      <div className={classes.node}>
        <UnstyledButton className={classes.toggle} onClick={() => setOpen((o) => !o)}>
          {open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
          <span>{summary}</span>
        </UnstyledButton>
        {open && (value.every(isPlainObject) ? (
          <ArrayOfObjectsTable rows={value as Record<string, unknown>[]} depth={depth} />
        ) : (
          <table className={classes.tbl} data-depth={depth % 2}>
            <tbody>
              {value.map((item, i) => (
                <tr key={i}>
                  <th className={classes.idx}>{i}</th>
                  <td>
                    <JsonNode value={item} depth={depth + 1} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    );
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className={classes.null}>{'{}'} · {t('json.empty')}</span>;
    const summary = `{${t('json.keys', { n: entries.length })}}`;
    return (
      <div className={classes.node}>
        {depth > 0 && (
          <UnstyledButton className={classes.toggle} onClick={() => setOpen((o) => !o)}>
            {open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            <span>{summary}</span>
          </UnstyledButton>
        )}
        {(open || depth === 0) && (
          <table className={classes.tbl} data-depth={depth % 2}>
            <tbody>
              {entries.map(([k, v]) => (
                <tr key={k}>
                  <th className={classes.k}>{k}</th>
                  <td>
                    <JsonNode value={v} depth={depth + 1} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
  return <Primitive value={value} />;
}

/** Array of objects → one column per key (union, first-seen order). */
function ArrayOfObjectsTable({ rows, depth }: { rows: Record<string, unknown>[]; depth: number }) {
  const cols: string[] = [];
  for (const r of rows) for (const k of Object.keys(r)) if (!cols.includes(k)) cols.push(k);
  return (
    <table className={classes.tbl} data-depth={depth % 2}>
      <thead>
        <tr>
          <th className={classes.idx}>#</th>
          {cols.map((c) => (
            <th key={c} className={classes.k}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <th className={classes.idx}>{i}</th>
            {cols.map((c) => (
              <td key={c}>
                {c in r ? <JsonNode value={r[c]} depth={depth + 1} /> : <Text span c="dimmed" size="xs">—</Text>}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
